import { CommonModule } from "@angular/common";
import { Component, Input, OnInit } from "@angular/core";
import { PrimeNgModule } from "../../../core/shared/primeng.module";
import { SharedComponents } from "../../shared.module";

/**
 * LineChartComponent is an Angular component responsible for rendering a line chart
 * using dynamic data passed through the `data` input property. The chart configuration
 * and data preparation are handled during initialization.
 */
@Component({
  selector: "app-line-chart",
  standalone: true,
  imports: [PrimeNgModule, CommonModule, ...SharedComponents],
  templateUrl: "./line-chart.component.html",
  styleUrl: "./line-chart.component.scss",
})
export class LineChartComponent implements OnInit {
  @Input() data: any;

  chartData: any;
  chartOptions: any;
  rootStyles = getComputedStyle(document.documentElement);

  /**
   * Lifecycle hook that initializes the component.
   * It prepares the chart data and configures the chart options.
   */
  ngOnInit(): void {
    if (!this.data || !this.data.data) return;
    this.prepareChartData();
    this.configureChartOptions();
  }

  /**
   * Prepares the chart data by extracting labels and datasets from the input `data`.
   * It creates the `chartData` object with labels and datasets for rendering the chart.
   */
  private prepareChartData(): void {
    const labels = Object.keys(this.data.data); // Extract dates
    const datasets = this.createDatasets(this.data.data);

    this.chartData = {
      labels,
      datasets,
    };
  }

  /**
   * Creates datasets for the line chart by iterating over the unique keys from the data.
   * Each dataset is associated with a color and represents a category like "pass" or "fail".
   * @param data - The input data object containing the test case results.
   * @returns An array of datasets used by the line chart.
   */
  private createDatasets(data: any): any[] {
    const result: any[] = [];
    const keys = this.getUniqueKeys();

    keys.forEach((key, index) => {
      const dataset = {
        label: key.charAt(0).toUpperCase() + key.slice(1), // Capitalize 'pass' and 'fail'
        data: Object.values(data).map((entry: any) => entry[key]), // Extract data for each key
        borderColor: this.getDynamicColor(index, key),
        backgroundColor: this.getDynamicColor(index, key),
        pointBackgroundColor: this.getDynamicColor(index, key),
        tension: 0.4,
      };
      result.push(dataset);
    });

    return result;
  }

  /**
   * Extracts the unique keys from the input data, which represent categories like "pass" or "fail".
   * @returns An array of unique category keys.
   */
  private getUniqueKeys() {
    const uniqueKeys = new Set<string>();

    Object.values(this.data?.data).forEach((entry: any) => {
      Object.keys(entry).forEach((key) => {
        uniqueKeys.add(key);
      });
    });

    return Array.from(uniqueKeys) || {};
  }

  /**
   * Configures the chart options such as scales, plugins, and title settings.
   * It sets the x and y axis titles, legend display, and grid colors.
   */
  private configureChartOptions(): void {
    this.chartOptions = {
      plugins: {
        datalabels: {
          display: false,
        },
        legend: {
          display: true,
          position: "top",
          labels: {
            usePointStyle: true,
            pointStyle: "circle",
            boxWidth: 8,
            boxHeight: 8,
            color: this.rootStyles.getPropertyValue("--dark-gray").trim(),
            font: {
              size: 10,
            },
          },
        },
      },
      title: {
        display: true,
        text: "",
        fontSize: 12,
        position: "right",
      },
      scales: {
        x: {
          title: {
            display: true,
            text: this.data?.chartMeta?.x_name || "Date",
            color: this.rootStyles.getPropertyValue("--dark-gray").trim(),
            font: {
              size: 10,
            },
          },
          ticks: {
            color: this.rootStyles.getPropertyValue("--dark-gray").trim(),
            autoSkip: true,
            maxRotation: 45,
            minRotation: 0,
          },
          grid: {
            color: this.rootStyles
              .getPropertyValue("--grey3")
              .trim(),
          },
        },
        y: {
          title: {
            display: true,
            text: this.data?.chartMeta?.y_name || "Test Case Count",
            color: this.rootStyles.getPropertyValue("--grey4").trim(),
            font: {
              size: 10,
            },
          },
          ticks: {
            color: this.rootStyles.getPropertyValue("--grey4").trim(),
            stepSize: 1,
            beginAtZero: true,
          },
          grid: {
            color: this.rootStyles
              .getPropertyValue("--grey3")
              .trim(),
          },
        },
      },
    };
  }

  /**
   * Returns a dynamic color for each dataset based on its index and key.
   * It cycles through a predefined set of colors for different categories.
   * @param index - The index of the dataset in the list of datasets.
   * @param key - The key representing the category (e.g., "pass", "fail").
   * @returns A color value as a string.
   */
  private getDynamicColor(index: number, key: string): string {
    console.log(key);
    const colors = [
      this.rootStyles.getPropertyValue("--cherry-red").trim(),
      this.rootStyles.getPropertyValue("--mint-green").trim(),
      this.rootStyles.getPropertyValue("--soft-orange").trim(),
      this.rootStyles.getPropertyValue("--lavender").trim(),
    ];
    return colors[index % colors.length];
  }

  /**
   * Validates if the input data is valid by checking if the `data` object exists
   * and contains entries.
   * @returns A boolean indicating whether the data is valid.
   */
  isDataValid(): boolean {
    return !!this.data?.data && Object.keys(this.data.data).length > 0;
  }
}
